import { useState } from 'react';

import useDisputes from 'api/useDisputes';
import useOrders from 'api/useOrders';
import { useUserDetails } from 'api/useUserDetails';
import { DisputeEvidenceType } from 'common/contracts';
import { EToasterStatus, showToast } from 'utils/showToast';

import Button from 'components/Button/Button';
import { FileUploader } from 'components/FileUploader';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { Modal } from 'components/Modal/Modal';
import { Order } from 'components/Order/types';
import Textarea from 'components/Textarea/Textarea';
export const DisputeEvidenceModal = ({
  isOpen,
  setIsOpen,
  order,
  publisherId,
  orderId
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  order: Order;
  publisherId: string;
  orderId: string;
}) => {
  const [name, setName] = useState('');
  const { userDetails } = useUserDetails();
  const username = userDetails?.userName;
  const [description, setDescription] = useState('');
  const [type, setType] = useState<DisputeEvidenceType | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const { addDisputeEvidence } = useDisputes(order.dispute?._id, publisherId);
  const { getOrderDetails } = useOrders(orderId, undefined, undefined, {
    selectedProjects: publisherId
  });

  const isAllowedFileType = (selectedFile: File) => {
    const allowedMimeTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
    const lowerName = selectedFile.name.toLowerCase();
    const hasAllowedExt = allowedExtensions.some((ext) =>
      lowerName.endsWith(ext)
    );
    return allowedMimeTypes.includes(selectedFile.type) || hasAllowedExt;
  };

  const handleAddEvidence = async () => {
    if ((order?.dispute?.evidences?.length || 0) >= 5) {
      showToast({
        message: 'You can upload up to 5 evidence files per dispute',
        status: EToasterStatus.ERROR
      });
      return;
    }
    if (!name || !description || !type || !file) {
      showToast({
        message: 'Please fill in all fields',
        status: EToasterStatus.ERROR
      });
      return;
    }
    await addDisputeEvidence.mutateAsync({
      username,
      description,
      type,
      file,
      name: name
    });
    getOrderDetails.refetch();
    showToast({
      message: 'Evidence added successfully',
      status: EToasterStatus.SUCCESS
    });
    setIsOpen(false);
    setName('');
    setDescription('');
    setType(null);
    setFile(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      width="600px"
      height="600px"
    >
      <div className="w-full h-full flex flex-col gap-2 overflow-auto p-1">
        <div className="flex mt-10 items-center justify-between">
          <h2 className="font-semibold text-base-primary">Dispute Evidence</h2>
        </div>
        <div className="text-base-secondary overflow-y-auto">
          <div className="text-sm text-base-mutedForeground">
            Upload supporting documentation or any details that could strengthen
            your case
          </div>
        </div>
        <div className="flex gap-4 mt-4">
          <Input
            className="w-1/2 [&_input]:focus:ring-0 [&_input]:focus-visible:ring-0 [&_input]:active:border-none"
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Add name"
            isRequired
            disabled={addDisputeEvidence.isLoading}
          />
          <InputSelectDropdown
            defaultSelected={type ? { label: type, value: type } : undefined}
            className="w-1/2"
            options={Object.values(DisputeEvidenceType).map((type) => ({
              label: type,
              value: type
            }))}
            onOptionChange={(value) => setType(value as DisputeEvidenceType)}
            placeholder="Select type"
            label="Type"
            disabled={addDisputeEvidence.isLoading}
          />
        </div>
        <div className="flex flex-col gap-2 mt-3">
          <FileUploader
            title="Upload Supporting Evidence"
            description="Will be scaled down to 32 × 32px"
            onFileSelect={(selected) => {
              if (!selected) return;
              if (!isAllowedFileType(selected)) {
                showToast({
                  message:
                    'Invalid file type. Allowed types: PDF, JPG, JPEG, PNG',
                  status: EToasterStatus.ERROR
                });
                return;
              }
              setFile(selected);
            }}
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            selectedFile={file}
            onFileRemove={() => setFile(null)}
            disabled={addDisputeEvidence.isLoading}
          />
        </div>
        <div className="flex flex-col gap-2 mt-3">
          <Textarea
            label="Description"
            placeholder="Add description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="w-full focus:ring-0 focus-visible:ring-0"
            disabled={addDisputeEvidence.isLoading}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => setIsOpen(false)}
          disabled={addDisputeEvidence.isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAddEvidence}
          isLoading={addDisputeEvidence.isLoading}
          disabled={addDisputeEvidence.isLoading}
        >
          Add Evidence
        </Button>
      </div>
    </Modal>
  );
};
